import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import VisibilityIcon from "@mui/icons-material/Visibility";

const ViewTopUpModal = ({ open, onClose, institute }) => {
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState("");
  const [currentTopupId, setCurrentTopupId] = useState(null);

  useEffect(() => {
    if (open && institute?.institue_id) {
      fetchTopups();
    }
  }, [open, institute]);

  const handleViewInvoice = async (topup_id) => {
    setInvoiceLoadingId(topup_id);
    setCurrentTopupId(topup_id);
    setInvoicePreviewOpen(true);
    setInvoicePreviewLoading(true);
    setInvoicePreviewError("");
    setInvoicePreviewUrl("");

    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/get_invoices_topup/${topup_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      const presignedInvoiceUrl = response.data?.invoice_url;
      if (presignedInvoiceUrl) {
        setInvoicePreviewUrl(presignedInvoiceUrl);
      } else {
        setInvoicePreviewError("No invoice URL available.");
      }
    } catch (err) {
      console.error("Error fetching invoice URL:", err);
      setInvoicePreviewError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to fetch invoice"
      );
    } finally {
      setInvoiceLoadingId(null);
      setInvoicePreviewLoading(false);
    }
  };
  const fetchTopups = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/get_topups/${institute.institue_id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      setTopups(response.data || []);
    } catch (err) {
      console.error("Error fetching top-ups:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          "Failed to fetch top-up data"
      );
      setTopups([]);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display (e.g., "2026-05-01" → "01 May 2026")
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const handleClose = () => {
    setTopups([]);
    setError("");
    onClose();
  };

  const handleCloseInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    setInvoicePreviewUrl("");
    setInvoicePreviewError("");
    setInvoicePreviewLoading(false);
    setCurrentTopupId(null);
  };

  const handleDownloadInvoice = async () => {
  if (!invoicePreviewUrl) return;

  try {
    const response = await axios.get(invoicePreviewUrl, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `TopUp_Invoice_${currentTopupId || ""}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Error downloading invoice:", err);
  }
};
  return (
    <React.Fragment>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
             View Top Up {!loading && ` (${topups.length})`}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={32} sx={{ color: "#3B82F6" }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontSize: "13px", color: "#EF4444" }}>
              {error}
            </Typography>
          </Box>
        ) : topups.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              No top-up records found for this institute.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#FAFAFA",
                    "& th": {
                      borderBottom: "1px solid #E5E7EB",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 1.5,
                      width: "60px",
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 1.5,
                    }}
                  >
                    Registration Limit
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 1.5,
                    }}
                  >
                    Top Up Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      py: 1.5,
                    }}
                  >
                    Invoice
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topups.map((topup, index) => (
                  <TableRow
                    key={topup.topup_id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                      "& td": {
                        borderBottom: "1px solid #F3F4F6",
                      },
                      "&:last-child td": {
                        borderBottom: "none",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        py: 1.5,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#1F1F1F",
                        fontWeight: 500,
                        py: 1.5,
                      }}
                    >
                      <Chip
                        label={topup.registration_limit}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          height: "24px",
                          backgroundColor: "#EFF6FF",
                          color: "#1D4ED8",
                          border: "none",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        color: "#6B7280",
                        py: 1.5,
                      }}
                    >
                      {formatDate(topup.topup_date)}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "13px",
                        py: 1.5,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleViewInvoice(topup.topup_id)}
                        disabled={invoiceLoadingId === topup.topup_id}
                        sx={{
                          color: "#3B82F6",
                          "&:hover": { backgroundColor: "#EFF6FF" },
                        }}
                      >
                        {invoiceLoadingId === topup.topup_id ? (
                          <CircularProgress size={16} sx={{ color: "#3B82F6" }} />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            fontSize: "13px",
            color: "#6B7280",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={invoicePreviewOpen}
      onClose={handleCloseInvoicePreview}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E5E7EB",
          pb: 2,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}>
          Invoice Preview {currentTopupId ? `- Top Up ${currentTopupId}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadInvoice}
            disabled={!invoicePreviewUrl || invoicePreviewLoading}
            sx={{
              backgroundColor: "#10B981",
              "&:hover": { backgroundColor: "#059669" },
              "&.Mui-disabled": {
                backgroundColor: "#9CA3AF",
                color: "#FFFFFF",
              },
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            Download
          </Button>
          <IconButton
            onClick={handleCloseInvoicePreview}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#F9FAFB",
        }}
      >
        {invoicePreviewLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Loading invoice...
            </Typography>
          </Box>
        ) : invoicePreviewError ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#EF4444" }}>
              {invoicePreviewError}
            </Typography>
          </Box>
        ) : invoicePreviewUrl ? (
          <iframe
            src={invoicePreviewUrl}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            title="Top Up Invoice Preview"
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              No invoice available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </React.Fragment>
  );
};

export default ViewTopUpModal;